import React from 'react'
import { useRouter } from 'next/router'
import { useEffect } from 'react'

import PageHeader from 'app/KinerjaPage/components/PageHeader'
import KinerjaPerencanaanApp from 'app/KinerjaPage/pages/Perencanaan'
import KinerjaPerencanaanWaitingApprovalApp from 'app/KinerjaPage/pages/PerencanaanWaitingApproval'
import { useAPIKinerja } from 'api/kinerja/useAPIKinerja'
import ContentProvider from 'app/KinerjaPage/components/ContentProvider'
import KinerjaPerencanaanApprovedApp from 'app/KinerjaPage/pages/PerencanaanApproved'
import { TOGGLE_KEYS } from 'configs/toggle-feature'
import { getServerSidePropsWithToggle } from 'utils/toggle.server'
import { PageGuardGuru } from 'app/KinerjaPage/components/PageGuardGuru'
import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import KinerjaLayout from 'app/KinerjaShared/components/KinerjaLayout'

import type { TToggleKeyValuePair } from 'configs/toggle-feature'
import type { NextPageContext } from 'next'
import type { Jenjang } from 'types/kinerja'

export const getServerSideProps = getServerSidePropsWithToggle({
  toggleKeys: TOGGLE_KEYS.filter((key) => key.includes('kinerja')),
  serverProps: async (
    _context: NextPageContext,
    toggle: TToggleKeyValuePair
  ) => {
    const isKinerjaEnabled = toggle?.['kinerja-enabled']
    const isKinerjaPerencanaanEnabled = toggle?.['kinerja-perencanaan-enabled']
    const isDeleteSKPEnabled = toggle?.['kinerja-delete-perencanaan-enabled']
    const isKinerjaPerencanaan2026Enabled =
      toggle?.['kinerja-perencanaan-2026-enabled']

    if (!isKinerjaEnabled || !isKinerjaPerencanaanEnabled) {
      return {
        redirect: {
          destination: '/404',
          permanent: false,
        },
      }
    }

    return {
      props: {
        isDeleteSKPEnabled,
        isKinerjaPerencanaan2026Enabled,
      },
    }
  },
})

const KinerjaPerencanaan = ({
  isDeleteSKPEnabled,
  isKinerjaPerencanaan2026Enabled,
}) => {
  const { periodYear } = usePeriodContext()
  const router = useRouter()
  const { data: { data: skp } = {}, isFetched: isSKPFetched } = useAPIKinerja()

  // For 2026 SKP, redirect to the appropriate page based on the state
  useEffect(() => {
    if (periodYear >= 2026 && isKinerjaPerencanaan2026Enabled && isSKPFetched) {
      const status = skp?.status
      if (['DRAFT', 'NOT_FOUND'].includes(status)) {
        router.replace(`${PENGELOLAAN_KINERJA_GURU_PATH}/perencanaan/ajukan`)
      } else if (status === 'APPROVED') {
        router.replace(`${PENGELOLAAN_KINERJA_GURU_PATH}/perencanaan/sepakati`)
      } else {
        router.replace(`${PENGELOLAAN_KINERJA_GURU_PATH}/perencanaan/rangkuman`)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodYear, skp?.status, isSKPFetched])

  return (
    <PageGuardGuru>
      <ContentProvider jenjang={skp?.jenjang as Jenjang}>
        {['WAITING_APPROVAL', 'REVISED'].includes(skp?.status) ? (
          <KinerjaPerencanaanWaitingApprovalApp
            skp={skp}
            isSKPFetched={isSKPFetched}
          />
        ) : ['APPROVED', 'READY'].includes(skp?.status) ? (
          <KinerjaPerencanaanApprovedApp
            skp={skp}
            isSKPFetched={isSKPFetched}
            isDeleteSKPEnabled={isDeleteSKPEnabled}
          />
        ) : (
          <KinerjaPerencanaanApp skp={skp} isSKPFetched={isSKPFetched} />
        )}
      </ContentProvider>
    </PageGuardGuru>
  )
}

KinerjaPerencanaan.Layout = KinerjaLayout

KinerjaPerencanaan.layoutOpts = {
  hasHeader: true,
  className: 'text-default bg-gray-10 !max-w-full !h-full min-h-screen',
  header: PageHeader,
}

export default KinerjaPerencanaan
